import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full h-10 rounded-lg border border-zinc-200 px-3 text-sm focus:border-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/5";

export function ChangePasswordDialog({ open, onOpenChange, forced }) {
  const { reloadUser, logout } = useAuth();
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.next !== f.confirm) return setError("New passwords do not match");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      toast.success("Password changed");
      setF({ current: "", next: "", confirm: "" });
      await reloadUser();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={forced ? () => {} : onOpenChange}>
      <DialogContent data-testid="change-password-dialog" className="max-w-md" onInteractOutside={(e) => forced && e.preventDefault()} hideClose={forced}>
        <DialogHeader>
          <DialogTitle className="font-display">{forced ? "Set your own password" : "Change password"}</DialogTitle>
          <DialogDescription>{forced ? "Your login was created by an administrator. Please choose a new password to continue." : "Use at least 8 characters."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="change-password-current-input" type="password" placeholder={forced ? "Temporary password" : "Current password"} className={inputCls} value={f.current} onChange={set("current")} />
          <input data-testid="change-password-new-input" type="password" placeholder="New password (min 8 characters)" className={inputCls} value={f.next} onChange={set("next")} />
          <input data-testid="change-password-confirm-input" type="password" placeholder="Confirm new password" className={inputCls} value={f.confirm} onChange={set("confirm")} />
          {error && <div data-testid="change-password-error" className="text-sm text-rose-600">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            {forced ? (
              <Button type="button" variant="ghost" data-testid="change-password-logout-button" onClick={logout}>Log out</Button>
            ) : (
              <Button type="button" variant="outline" data-testid="change-password-cancel-button" onClick={() => onOpenChange(false)}>Cancel</Button>
            )}
            <Button data-testid="change-password-submit-button" disabled={busy || !f.current || f.next.length < 8 || !f.confirm}>
              {busy ? "Saving…" : "Update password"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
